/**
 * Add Node › This phone, inside the noBGP app (mobile/). The app's NobgpShell.nodeStatus answers
 * the agent's state, and while an unregistered agent waits, the link of its registration: the same
 * link `nobgp register` prints on any other machine. The person approves the phone there, and
 * chooses the network and the name. No key is involved.
 *
 * Pure, so `npm test` checks it; AddThisPhone.tsx applies it.
 */

export type PhoneNodeStatus = { state?: string; approve_url?: string; node_id?: string; error?: string }

/** What the section shows. */
/** `none`: no answer yet, or an app build with no node (its nodeStatus rejected): show nothing. */
export type PhoneNodeView = 'none' | 'off' | 'waiting' | 'approve' | 'node' | 'stopped' | 'failed'

export const phoneNodeView = (s: PhoneNodeStatus | null, origin = ''): PhoneNodeView => {
  if (!s || s.state === 'unavailable') return 'none'
  switch (s.state) {
    case undefined:
    case 'off':
      // A registered phone whose VPN was turned off (Android's onRevoke) offers Turn on, as stopped.
      return s.node_id ? 'stopped' : 'off'
    case 'running':
      return 'node'
    case 'stopped':
      return 'stopped'
    case 'failed':
    case 'crashed':
      return 'failed'
  }
  return approvalPath(s.approve_url, origin) ? 'approve' : 'waiting'
}

/**
 * The console's own registration page for an approval link, or null for anything else.
 *
 * ⚠ Only `/register/<code>`, on this console's origin or on the noBGP cloud's short host
 * (`https://r.nobgp.com/<code>`, which redirects there): the console navigates inside itself and
 * never to a host the link names.
 */
export const approvalPath = (link: string | undefined, origin = ''): string | null => {
  if (!link) return null
  let url: URL
  try {
    url = new URL(link)
  } catch {
    return null
  }
  const code = /^[A-Za-z0-9]{4,16}$/
  const parts = url.pathname.split('/').filter(Boolean)
  if (url.protocol === 'https:' && url.host === 'r.nobgp.com' && parts.length === 1 && code.test(parts[0])) {
    return `/register/${parts[0]}`
  }
  if (url.origin === origin && parts.length === 2 && parts[0] === 'register' && code.test(parts[1])) {
    return `/register/${parts[1]}`
  }
  return null
}
